import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Package, Plus, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/ui/data-table';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { itemBundleApi, queryFn, type ItemBundle } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { BundleDialog } from '@/components/BundleDialog';

function ItemBundlesContent() {
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState<ItemBundle | null>(null);

  const { data: bundles = [], isLoading } = useQuery({
    queryKey: ['item-bundles', currentBusiness?.id],
    queryFn: () => queryFn(itemBundleApi.list(currentBusiness!.id)),
    enabled: !!currentBusiness,
  });

  const invalidateBundles = () => queryClient.invalidateQueries({ queryKey: ['item-bundles'] });

  const handleAddBundle = () => {
    setEditingBundle(null);
    setDialogOpen(true);
  };

  const handleEditBundle = (bundle: ItemBundle) => {
    setEditingBundle(bundle);
    setDialogOpen(true);
  };

  const handleDeleteBundle = async (bundleId: string) => {
    if (!confirm('Delete this bundle?')) return;
    await itemBundleApi.delete(bundleId);
    invalidateBundles();
  };


  const calculateBundleTotal = (bundle: ItemBundle) => {
    return bundle.links.reduce((sum, link) => {
      return sum + parseFloat(link.quantity) * parseFloat(link.item_rate);
    }, 0);
  };

  const columns: ColumnDef<ItemBundle>[] = [
    {
      accessorKey: 'name',
      header: 'Bundle Name',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.name}</div>
          {row.original.description && (
            <div className="text-xs text-muted-foreground truncate">{row.original.description}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'links',
      header: 'Items',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.links.length} item{row.original.links.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      id: 'total',
      header: 'Total',
      cell: ({ row }) => (
        <div className="text-right text-sm font-medium">
          {formatAmount(calculateBundleTotal(row.original).toString())}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditBundle(row.original);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBundle(row.original.id);
                }}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage>
          <ListPageHeader>
            <ListPageTitle>Item Bundles</ListPageTitle>
            <ListPageActions>
              {!isViewOnly && (
                <Button onClick={handleAddBundle} className="rounded-full">
                  <Plus className="h-4 w-4" />
                  Add Bundle
                </Button>
              )}
            </ListPageActions>
          </ListPageHeader>

          <ListPageCard>
            <ListPageContent>
              <DataTable
                columns={columns}
                data={bundles}
                loading={isLoading}
                onRowClick={(bundle) => handleEditBundle(bundle)}
                noBorder
                emptyIcon={<Package className="h-6 w-6 text-muted-foreground" />}
                emptyTitle="No bundles yet"
                emptyDescription="Create your first bundle to get started."
              />
            </ListPageContent>
          </ListPageCard>
        </ListPage>

      <BundleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        businessId={currentBusiness?.id || ''}
        bundle={editingBundle}
        onSaved={invalidateBundles}
      />
    </>
  );
}

export function ItemBundles() {
  return <ItemBundlesContent />;
}
